import { useEffect, useState } from 'react';
import { Check, X, ArrowRight, RefreshCw, Trophy, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import type { Quiz, QuizQuestion } from '@/data/learnQuizzes';
import { useQuizAttempts } from '@/hooks/useQuizAttempts';
import { useLearnProgress } from '@/hooks/useLearnProgress';

type Props = {
  quiz: Quiz;
  onBack?: () => void;
};

export function QuizRunner({ quiz, onBack }: Props) {
  const { recordAttempt, bestForQuiz } = useQuizAttempts();
  const { isComplete, toggle } = useLearnProgress();

  const [index, setIndex] = useState(0);
  const [selected, setSelected] = useState<number | null>(null);
  const [revealed, setRevealed] = useState(false);
  const [answers, setAnswers] = useState<boolean[]>([]);
  const [finished, setFinished] = useState(false);

  const total = quiz.questions.length;
  const q: QuizQuestion = quiz.questions[index];
  const score = answers.filter(Boolean).length;
  const pct = total ? score / total : 0;
  const passed = pct >= quiz.passThreshold;
  const best = bestForQuiz(quiz.slug);

  const submit = () => {
    if (selected == null || revealed) return;
    const correct = selected === q.answer;
    setRevealed(true);
    setAnswers((prev) => [...prev, correct]);
  };

  const next = () => {
    if (index + 1 < total) {
      setIndex(index + 1);
      setSelected(null);
      setRevealed(false);
    } else {
      setFinished(true);
    }
  };

  // When finished, record the attempt and update path progress
  useEffect(() => {
    if (!finished) return;
    void recordAttempt(quiz.slug, score, total, passed);
    if (passed && !isComplete(quiz.lessonKey)) {
      toggle(quiz.lessonKey);
      toast.success(`Passed ${quiz.title}! Path progress updated.`);
    } else if (passed) {
      toast.success(`Nice — ${score}/${total} correct.`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finished]);

  const restart = () => {
    setIndex(0);
    setSelected(null);
    setRevealed(false);
    setAnswers([]);
    setFinished(false);
  };

  // ─── Results screen ─────────────────────────────────────────
  if (finished) {
    return (
      <div className="rounded-xl border border-border bg-card p-5 animate-fade-in">
        <div className="flex items-center gap-2 mb-3">
          <Trophy className={`h-6 w-6 ${passed ? 'text-ink-amber' : 'text-muted-foreground'}`} />
          <h3 className="font-display text-lg font-semibold text-foreground">
            {passed ? 'You passed!' : 'Almost there'}
          </h3>
        </div>
        <p className="text-3xl font-display font-bold text-foreground">
          {score}<span className="text-base text-muted-foreground font-body"> / {total}</span>
        </p>
        <p className="mt-1 text-xs text-muted-foreground font-body">
          {Math.round(pct * 100)}% correct · pass threshold {Math.round(quiz.passThreshold * 100)}%
        </p>

        {!passed && (
          <p className="mt-3 text-xs text-foreground/80 font-body leading-relaxed">
            Review the lessons in this path and try again — passing marks the quiz lesson complete in your path progress.
          </p>
        )}

        <div className="mt-4 flex gap-2">
          <button
            type="button"
            onClick={restart}
            className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-xs font-medium text-primary-foreground font-body hover:bg-primary/90 transition-colors"
          >
            <RefreshCw className="h-3.5 w-3.5" /> Retry
          </button>
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              className="inline-flex items-center gap-1.5 rounded-lg bg-secondary px-3 py-2 text-xs font-medium text-foreground font-body hover:bg-muted transition-colors"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Back to quizzes
            </button>
          )}
        </div>
      </div>
    );
  }

  // ─── Question screen ────────────────────────────────────────
  return (
    <div className="rounded-xl border border-border bg-card p-4 animate-fade-in">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-body">
          {quiz.title}
        </p>
        <p className="text-[10px] text-muted-foreground font-body">
          {index + 1} / {total} {best && <span className="ml-2">· best {best.score}/{best.total}</span>}
        </p>
      </div>
      <div className="h-1 rounded-full bg-secondary overflow-hidden mb-3">
        <div className="h-full bg-primary transition-all" style={{ width: `${((index + (revealed ? 1 : 0)) / total) * 100}%` }} />
      </div>

      <h3 className="text-sm font-semibold text-foreground font-body leading-snug mb-3">
        {q.prompt}
      </h3>

      {q.type === 'choice' ? (
        <div className="space-y-2">
          {q.options.map((opt, i) => {
            const isPick = selected === i;
            const isAnswer = i === q.answer;
            const state = !revealed
              ? isPick
                ? 'border-primary bg-primary/10 text-foreground'
                : 'border-border text-foreground hover:border-primary/40'
              : isAnswer
                ? 'border-ink-emerald bg-ink-emerald/15 text-foreground'
                : isPick
                  ? 'border-ink-ruby bg-ink-ruby/15 text-foreground'
                  : 'border-border text-muted-foreground';
            return (
              <button
                key={i}
                type="button"
                onClick={() => !revealed && setSelected(i)}
                disabled={revealed}
                className={`w-full text-left rounded-lg border px-3 py-2.5 text-xs font-body transition-colors flex items-center gap-2 ${state}`}
              >
                {revealed && isAnswer && <Check className="h-3.5 w-3.5 text-ink-emerald shrink-0" />}
                {revealed && !isAnswer && isPick && <X className="h-3.5 w-3.5 text-ink-ruby shrink-0" />}
                <span className="flex-1">{opt}</span>
              </button>
            );
          })}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          {q.options.map((opt, i) => {
            const isPick = selected === i;
            const isAnswer = i === q.answer;
            const ring = !revealed
              ? isPick
                ? 'ring-2 ring-primary'
                : 'ring-1 ring-border hover:ring-primary/40'
              : isAnswer
                ? 'ring-2 ring-ink-emerald'
                : isPick
                  ? 'ring-2 ring-ink-ruby'
                  : 'ring-1 ring-border opacity-60';
            return (
              <button
                key={i}
                type="button"
                onClick={() => !revealed && setSelected(i)}
                disabled={revealed}
                className={`rounded-lg overflow-hidden bg-secondary/40 p-1.5 transition ${ring}`}
              >
                <img
                  src={opt.src}
                  alt={opt.label}
                  loading="lazy"
                  className="w-full aspect-[2/3] object-cover rounded"
                />
                <p className="mt-1 text-[10px] text-center text-foreground font-body">{opt.label}</p>
              </button>
            );
          })}
        </div>
      )}

      {revealed && (
        <div className="mt-3 rounded-lg bg-secondary/60 p-3">
          <p className="text-[11px] font-medium text-foreground font-body">
            {selected === q.answer ? '✓ Correct' : '✗ Not quite'}
          </p>
          <p className="mt-1 text-[11px] text-muted-foreground font-body leading-snug">{q.explain}</p>
        </div>
      )}

      <div className="mt-4 flex items-center justify-between">
        {onBack ? (
          <button
            type="button"
            onClick={onBack}
            className="text-[11px] text-muted-foreground font-body hover:text-foreground"
          >
            Cancel
          </button>
        ) : (
          <span />
        )}
        {!revealed ? (
          <button
            type="button"
            onClick={submit}
            disabled={selected == null}
            className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-xs font-medium text-primary-foreground font-body disabled:opacity-40 hover:bg-primary/90 transition-colors"
          >
            Submit
          </button>
        ) : (
          <button
            type="button"
            onClick={next}
            className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-xs font-medium text-primary-foreground font-body hover:bg-primary/90 transition-colors"
          >
            {index + 1 < total ? 'Next' : 'See results'} <ArrowRight className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

export function QuizListItem({
  quiz,
  onStart,
}: {
  quiz: Quiz;
  onStart: () => void;
}) {
  const { bestForQuiz, hasPassed } = useQuizAttempts();
  const best = bestForQuiz(quiz.slug);
  const passed = hasPassed(quiz.slug);
  return (
    <button
      type="button"
      onClick={onStart}
      className="w-full rounded-xl border border-border bg-card p-4 text-left hover:border-primary/40 transition-colors"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="font-display text-sm font-semibold text-foreground">{quiz.title}</h3>
          <p className="mt-0.5 text-[11px] text-muted-foreground font-body">{quiz.blurb}</p>
        </div>
        {passed && (
          <span className="inline-flex items-center gap-1 rounded-full bg-ink-emerald/15 px-2 py-0.5 text-[10px] font-medium text-ink-emerald font-body shrink-0">
            <Check className="h-3 w-3" /> Passed
          </span>
        )}
      </div>
      <div className="mt-2 flex items-center justify-between text-[10px] text-muted-foreground font-body">
        <span>{quiz.questions.length} questions · pass {Math.round(quiz.passThreshold * 100)}%</span>
        <span>{best ? `Best ${best.score}/${best.total}` : 'Not attempted'}</span>
      </div>
    </button>
  );
}
